import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useCalendar } from '@/hooks/useCalendar';
import { CalendarHeader } from '@/components/CalendarHeader';
import { MonthView } from '@/components/MonthView';
import { WeekView } from '@/components/WeekView';
import { DayView } from '@/components/DayView';
import { CalendarStats } from '@/components/CalendarStats';
import { GoogleCalendarImport } from '@/components/GoogleCalendarImport';
import { navigateNext, navigatePrevious } from '@/lib/calendar';
import { Download } from 'lucide-react';
import { addDays, subDays } from 'date-fns';

export const CalendarPage = () => {
  const { user } = useAuth();
  const {
    currentDate,
    setCurrentDate,
    view,
    setView,
    records,
    loading,
    calendarDays,
  } = useCalendar(user?.uid || '');

  const [showImport, setShowImport] = useState(false);

  const handleNavigatePrevious = () => {
    setCurrentDate(navigatePrevious(currentDate, view));
  };

  const handleNavigateNext = () => {
    setCurrentDate(navigateNext(currentDate, view));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDayClick = (date: Date) => {
    setCurrentDate(date);
    setView('day');
  };

  const handleDayNavigate = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentDate(subDays(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* ページタイトル */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
            カレンダー
          </h1>
          <p className="text-gray-600 mt-2">稼働時間をカレンダー形式で確認できます</p>
        </div>
        <button
          onClick={() => setShowImport(!showImport)}
          className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md flex items-center gap-2"
        >
          <Download className="w-5 h-5" />
          {showImport ? 'カレンダーに戻る' : 'Googleカレンダーから取込'}
        </button>
      </div>

      {showImport ? (
        /* Googleカレンダーインポート */
        <GoogleCalendarImport />
      ) : (
        <>
          {/* 統計カード */}
          <CalendarStats records={records} />

          {/* カレンダーヘッダー */}
          {view !== 'day' && (
            <CalendarHeader
              currentDate={currentDate}
              view={view}
              onNavigatePrevious={handleNavigatePrevious}
              onNavigateNext={handleNavigateNext}
              onViewChange={setView}
              onToday={handleToday}
            />
          )}

          {/* カレンダービュー */}
          {view === 'month' && (
            <MonthView days={calendarDays} onDayClick={handleDayClick} />
          )}
          {view === 'week' && (
            <WeekView days={calendarDays} onDayClick={handleDayClick} />
          )}
          {view === 'day' && (
            <DayView
              day={calendarDays.find((d) => 
                d.date.toDateString() === currentDate.toDateString()
              ) || { date: currentDate, records: [], totalMinutes: 0, isCurrentMonth: true, isToday: false }}
              onNavigate={handleDayNavigate}
              onViewChange={setView}
            />
          )}
        </>
      )}
    </div>
  );
};
